// src/pages/storePages/ProductDetailPage.js
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { storeGetProductBySlug } from "../../storeAPIs/StoreProductsAPIs";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import Breadcrumb from "../../components/store/Breadcrumb";
import "../../static/storecss/ProductDetailPage.css";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { items, addItem } = useCart();
  const { isLoggedIn } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setActiveImageIndex(0);

    storeGetProductBySlug(slug)
      .then((res) => {
        if (cancelled) return;
        setProduct(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setError("Product not found.");
        } else {
          setError(err.message || "Failed to load product.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  /* ── LOADING — skeleton that mirrors the real layout ── */
  if (loading) {
    return (
      <div
        className="prod-detail-page-wrapper"
        aria-busy="true"
        aria-live="polite"
      >
        <div className="prod-detail-page-skel-breadcrumb" aria-hidden="true">
          <span className="prod-detail-page-skel-block prod-detail-page-skel-w-40" />
          <span className="prod-detail-page-skel-block prod-detail-page-skel-w-60" />
          <span className="prod-detail-page-skel-block prod-detail-page-skel-w-80" />
        </div>

        <div className="prod-detail-page-grid" aria-hidden="true">
          <div className="prod-detail-page-gallery">
            <div className="prod-detail-page-skel-gallery" />
            <div className="prod-detail-page-skel-thumbs">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="prod-detail-page-skel-thumb" />
              ))}
            </div>
          </div>

          <div className="prod-detail-page-info-panel">
            <div className="prod-detail-page-skel-block prod-detail-page-skel-title" />
            <div className="prod-detail-page-skel-block prod-detail-page-skel-title-short" />
            <div className="prod-detail-page-skel-block prod-detail-page-skel-price" />
            <div className="prod-detail-page-skel-block prod-detail-page-skel-stock" />

            <div className="prod-detail-page-skel-cta-block" />

            <div className="prod-detail-page-skel-block prod-detail-page-skel-desc" />
            <div className="prod-detail-page-skel-block prod-detail-page-skel-desc" />
            <div className="prod-detail-page-skel-block prod-detail-page-skel-desc-short" />

            <div className="prod-detail-page-skel-actions">
              <div className="prod-detail-page-skel-btn" />
              <div className="prod-detail-page-skel-btn" />
            </div>
          </div>
        </div>

        <div className="prod-detail-page-specs" aria-hidden="true">
          <div className="prod-detail-page-skel-block prod-detail-page-skel-section-title" />
          <div className="prod-detail-page-skel-spec-rows">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="prod-detail-page-skel-spec-row">
                <div className="prod-detail-page-skel-block prod-detail-page-skel-spec-key" />
                <div className="prod-detail-page-skel-block prod-detail-page-skel-spec-value" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ── ERROR ── */
  if (error) {
    const isNotFound = error === "Product not found.";

    return (
      <div className="prod-detail-page-wrapper">
        <div className="prod-detail-page-error">
          <div className="prod-detail-page-error-icon" aria-hidden="true">
            <i className="fa-solid fa-circle-exclamation" />
          </div>
          <h2 className="prod-detail-page-error-title">
            {isNotFound
              ? "We couldn't find this product"
              : "Something went wrong"}
          </h2>
          <p className="prod-detail-page-error-text">{error}</p>
          <div className="prod-detail-page-error-actions">
            <button
              type="button"
              className="prod-detail-page-error-btn prod-detail-page-error-btn-primary"
              onClick={() => navigate("/store")}
            >
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              Back to store
            </button>
            <button
              type="button"
              className="prod-detail-page-error-btn prod-detail-page-error-btn-ghost"
              onClick={() => window.location.reload()}
            >
              <i className="fa-solid fa-rotate-right" aria-hidden="true" />
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const images = product.images ?? [];
  const specs = product.specs ?? [];
  const activeImage = images[activeImageIndex];
  const inStock = product.in_stock ?? product.stock > 0;
  const linkedProject = product.linked_project;

  const cartLine = items.find((i) => i.product_id === product.id);
  const alreadyInCart = Boolean(cartLine);

  const breadcrumbItems = [
    { label: "Store", to: "/store" },
    (product.category?.slug || product.category_slug) && {
      label: product.category?.name ?? product.category,
      to: `/store?category=${product.category?.slug ?? product.category_slug}`,
    },
    (product.subcategory?.slug || product.subcategory_slug) && {
      label: product.subcategory?.name ?? product.subcategory,
      to: `/store?category=${
        product.subcategory?.slug ?? product.subcategory_slug
      }`,
    },
    { label: product.name },
  ].filter(Boolean);

  const renderSpecValue = (value) => {
    if (value == null || value === "") return null;

    const text = String(value)
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n");

    const normalized = text.replace(/->/g, "\n->");

    const rawLines = normalized
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    const bulletRegex = /^(->|•|·|-{1,2}\s|\*\s)/;
    const hasBullets = rawLines.some((l) => bulletRegex.test(l));

    if (hasBullets) {
      const items = rawLines.map((line) =>
        line.replace(bulletRegex, "").trim()
      );
      return (
        <ul className="prod-detail-page-spec-bullets">
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    }

    return rawLines.map((line, i) => (
      <span key={i} className="prod-detail-page-spec-line">
        {line}
      </span>
    ));
  };

  const handleAddToCart = async () => {
    if (!inStock || adding || alreadyInCart) return;

    if (!isLoggedIn) {
      navigate(`/login?next=/store/product/${product.slug}`);
      return;
    }

    setAdding(true);
    try {
      await addItem(product, 1);
    } catch (err) {
    } finally {
      setAdding(false);
    }
  };

  const handleBuyNow = async () => {
    if (!inStock || adding) return;

    if (!isLoggedIn) {
      navigate(`/login?next=/store/product/${product.slug}`);
      return;
    }

    setAdding(true);
    try {
      if (!alreadyInCart) {
        await addItem(product, 1);
      }
      navigate("/checkout");
    } catch (err) {
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="prod-detail-page-wrapper">
      <Breadcrumb items={breadcrumbItems} />

      <div className="prod-detail-page-grid">
        {/* ── LEFT: gallery ── */}
        <div className="prod-detail-page-gallery">
          <div className="prod-detail-page-gallery-main">
            {activeImage?.image_url || product.primary_image ? (
              <img
                src={activeImage?.image_url || product.primary_image}
                alt={activeImage?.alt_text || product.name}
              />
            ) : (
              <div className="prod-detail-page-gallery-placeholder">
                <i className="fa-solid fa-image" />
              </div>
            )}
          </div>

          {images.length > 1 && (
            <div className="prod-detail-page-gallery-thumbs">
              {images.map((img, idx) => (
                <button
                  key={img.id}
                  type="button"
                  className={`thumb ${
                    idx === activeImageIndex ? "active" : ""
                  }`}
                  onClick={() => setActiveImageIndex(idx)}
                  onMouseEnter={() => setActiveImageIndex(idx)}
                >
                  <img
                    src={img.image_url}
                    alt={img.alt_text || `${product.name} ${idx + 1}`}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── RIGHT: info ── */}
        <div className="prod-detail-page-info-panel">
          <h1 className="prod-detail-page-title">{product.name}</h1>

          <div className="prod-detail-page-price-row">
            <span className="price">
              <span className="currency">KES</span>{" "}
              {Number(product.price).toLocaleString()}
            </span>
          </div>

          <div
            className={`prod-detail-page-stock ${
              inStock ? "in" : "out"
            }`}
          >
            {inStock ? (
              <>
                <i className="fa-solid fa-check-circle" /> In stock
                {product.stock <= 5 && ` (only ${product.stock} left)`}
              </>
            ) : (
              <>
                <i className="fa-solid fa-times-circle" /> Out of stock
              </>
            )}
          </div>

          {linkedProject && (
          <Link
            to={`/course/${linkedProject.slug || linkedProject.id}`}
            className="prod-detail-page-linked-project"
          >
              <span
                className="prod-detail-page-linked-project-icon"
                aria-hidden="true"
              >
                <i className="fa-solid fa-graduation-cap" />
              </span>

              <span className="prod-detail-page-linked-project-body">
                <span className="prod-detail-page-linked-project-cta">
                  <i className="fa-solid fa-bolt" aria-hidden="true" />
                  Use this to learn
                </span>
                <span className="prod-detail-page-linked-project-title">
                  {linkedProject.title}
                </span>
              </span>

              <i
                className="fa-solid fa-arrow-right prod-detail-page-linked-project-arrow"
                aria-hidden="true"
              />
            </Link>
          )}

          {product.description && (
            <p className="prod-detail-page-description">
              {product.description}
            </p>
          )}

          <div className="prod-detail-page-actions">
            <button
              type="button"
              className="prod-detail-page-btn-primary"
              disabled={!inStock || adding || alreadyInCart}
              onClick={handleAddToCart}
            >
              {alreadyInCart ? (
                <>
                  <i className="fa-solid fa-check" /> Already in cart
                </>
              ) : (
                <>
                  <i className="fa-solid fa-cart-shopping" />{" "}
                  {adding ? "Adding…" : "Add to cart"}
                </>
              )}
            </button>

            <button
              type="button"
              className="prod-detail-page-btn-secondary"
              disabled={!inStock || adding}
              onClick={handleBuyNow}
            >
              Buy now
            </button>
          </div>
        </div>
      </div>

      {/* ── Specs ── */}
      {specs.length > 0 && (
        <section className="prod-detail-page-specs">
          <h2>Specifications</h2>
          <table className="prod-detail-page-specs-table">
            <tbody>
              {specs.map((spec) => (
                <tr key={spec.id}>
                  <th>{spec.key}</th>
                  <td>{renderSpecValue(spec.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}